import React from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, FileTextIcon, FolderOpenIcon, AlertTriangleIcon, MailIcon, ClockIcon, ChevronRightIcon, ChevronDownIcon, ExternalLinkIcon, AlertCircleIcon, UsersIcon, SettingsIcon, FileIcon, QuoteIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { CHALLENGE_IMAGES, challengeCopy, frictionSteps, evidenceStats, testimonial, insights, type Bi } from '../../data/permisChallenge';
const EASE = [0.23, 1, 0.32, 1] as const;
const STEP_ICONS = {
  search: SearchIcon,
  form: FileTextIcon,
  folder: FolderOpenIcon,
  warn: AlertTriangleIcon,
  mail: MailIcon,
  clock: ClockIcon
} as const;
const INSIGHT_ICONS = {
  people: UsersIcon,
  gear: SettingsIcon,
  doc: FileIcon
} as const;
const CARD = 'rounded-2xl border border-[#ECECF4] bg-white shadow-[0_18px_40px_-30px_rgba(40,36,120,0.45)]';
function SourceLink({
  label,
  href
}: {
  label: string;
  href?: string;
}) {
  if (!href) return <span className="font-sans text-[14px] text-[#6B7180]" data-id="element-4876" data-mp="37a13smfa012z">{label}</span>;
  return <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-sans text-[14px] text-[#6B7180] hover:text-[#4F46E5] transition-colors duration-150" data-id="element-4877" data-mp="12lc4clw83gav">
      {label}
      <ExternalLinkIcon className="w-3.5 h-3.5 shrink-0" strokeWidth={2} aria-hidden="true" data-id="element-4878" data-mp="1gfcuwsd0tx05" />
    </a>;
}
export function PermisChallenge() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const reveal = {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    },
    transition: {
      duration: 0.3,
      ease: EASE
    }
  };
  return <div className="max-w-[1640px] mx-auto" data-id="element-4879" data-mp="xm8xcvwon009">
      {/* Top: intro + citizen story */}
      <div className="grid grid-cols-1 xl:grid-cols-[39fr_61fr] gap-10 xl:gap-8 items-start" data-id="element-4880" data-mp="243w5jejjmlve">
        <motion.div {...reveal} className="min-w-0" data-id="element-4881" data-mp="291xm9sthy93p">
          <p className="font-sans text-[13px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-4882" data-mp="90zgu77tzicx">{pick(challengeCopy.eyebrow)}</p>
          <h2 className="font-serif text-[2.6rem] sm:text-[3.5rem] 2xl:text-[4.2rem] leading-[1.02] tracking-tight mt-5" data-id="element-4883" data-mp="4yw0p5a7vplb">
            <span className="block text-[#121A3A]" data-id="element-4884" data-mp="ixchxq5pgyhp">{pick(challengeCopy.line1)}</span>
            <span className="block text-[#4F46E5]" data-id="element-4885" data-mp="e0vhbwhbkrnq">{pick(challengeCopy.line2)}</span>
            <span className="block text-[#4F46E5]" data-id="element-4886" data-mp="3mgxbw6jifehk">{pick(challengeCopy.line3)}</span>
          </h2>
          <p className="font-sans text-[17px] md:text-[19px] text-[#5B6478] leading-relaxed mt-7 max-w-[600px]" data-id="element-4887" data-mp="3hidoe1p9pun3">{pick(challengeCopy.body)}</p>
        </motion.div>

        <motion.div {...reveal} transition={{
        duration: 0.3,
        delay: 0.05,
        ease: EASE
      }} className="relative min-w-0" data-id="element-4888" data-mp="3rfida9ai047l">
          {/* Photo + floating cards */}
          <div className="relative h-[300px] sm:h-[340px] xl:h-[270px]" data-id="element-4889" data-mp="33ntimma7f3nh">
            <img src={CHALLENGE_IMAGES.citizen} alt="" className="absolute right-0 top-0 h-full w-full xl:w-[78%] object-cover object-[70%_30%] rounded-3xl xl:rounded-none" style={{
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 30%, #000 100%)',
            maskImage: 'linear-gradient(to right, transparent 0%, #000 30%, #000 100%)'
          }} data-id="element-4890" data-mp="2yprwttle8q8b" />
            <div className={`${CARD} absolute left-0 top-4 sm:top-6 rounded-[28px] p-2.5 pr-6 flex items-center gap-4 max-w-[400px]`} data-id="element-4891" data-mp="1wz9xp5wzpb27">
              <img src={CHALLENGE_IMAGES.avatar} alt="" className="w-[60px] h-[60px] rounded-full object-cover shrink-0" data-id="element-4892" data-mp="21wtm2ynej47v" />
              <p className="font-sans text-[15px] text-[#121A3A] leading-snug" data-id="element-4893" data-mp="1ny2bvnbnu2p0">{pick(challengeCopy.question)}</p>
            </div>
            <div className={`${CARD} absolute right-3 top-4 sm:top-12 rounded-2xl px-4 py-3 flex items-center gap-3 max-w-[210px] ring-4 ring-[#FDE7EA]`} data-id="element-4894" data-mp="cj6mb6bh6gto">
              <AlertCircleIcon className="w-9 h-9 text-white fill-[#EF4462] shrink-0" strokeWidth={2} aria-hidden="true" data-id="element-4895" data-mp="hjungktf4e1m" />
              <p className="font-sans text-[14px] font-medium text-[#121A3A] leading-snug" data-id="element-4896" data-mp="wtzoxto36l7o">{pick(challengeCopy.warning)}</p>
            </div>
          </div>

          {/* Friction journey */}
          <ol className="relative mt-4 xl:-mt-4 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 xl:gap-5" data-id="element-4897" data-mp="n00pj2fedfjf">
            {frictionSteps.map((s, i) => {
            const Icon = STEP_ICONS[s.key];
            const warn = s.key === 'warn';
            return <li key={s.key} className="relative" data-id="element-4898" data-mp="1aszdl1bln03u" data-mpi={i}>
                  <div className={`${CARD} h-full px-4 py-4 flex flex-col`} data-id="element-4899" data-mp="16q2rv1qrinvo">
                    <span className={`w-10 h-10 rounded-full flex items-center justify-center ${warn ? 'bg-[#FDECEF]' : 'bg-[#F1F0FD]'}`} aria-hidden="true" data-id="element-4900" data-mp="16s701vvp291r">
                      <Icon className={`w-5 h-5 ${warn ? 'text-[#E23B57]' : 'text-[#4F46E5]'}`} strokeWidth={1.8} data-id="element-4901" data-mp="3mhi78rypkmwy" />
                    </span>
                    <p className="font-sans text-[14px] font-semibold text-[#121A3A] leading-snug mt-3" data-id="element-4902" data-mp="220hbmumfwmnn">{pick(s.title)}</p>
                    <p className="font-sans text-[12px] text-[#6B7180] leading-snug mt-2" data-id="element-4903" data-mp="26w6h1el54xrv">{pick(s.sub)}</p>
                  </div>
                  {i < frictionSteps.length - 1 && <ChevronRightIcon className="hidden xl:block absolute -right-[18px] top-1/2 -translate-y-1/2 w-4 h-4 text-[#4F46E5]" strokeWidth={2.4} aria-hidden="true" data-id="element-4904" data-mp="2fs5o4z0lpd55" />}
                </li>;
          })}
          </ol>
        </motion.div>
      </div>

      {/* Evidence + testimonial */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-[1fr_1fr_1fr_2fr] gap-5" data-id="element-4905" data-mp="2yonmhz9ygbad">
        {evidenceStats.map((s, i) => <motion.article key={i} {...reveal} transition={{
        duration: 0.3,
        delay: i * 0.05,
        ease: EASE
      }} className={`${CARD} p-6 flex flex-col`} data-id="element-4906" data-mp="14aw68gx4et5y" data-mpi={i}>
            {s.prefix && <p className="font-sans text-[15px] font-semibold text-[#4F46E5]" data-id="element-4907" data-mp="27ofq8j1k9gli">{pick(s.prefix)}</p>}
            <p className="font-sans text-[2.6rem] font-bold text-[#4F46E5] leading-none tracking-tight" data-id="element-4908" data-mp="22pcwvvuy0ytd">{pick(s.value)}</p>
            <p className="font-sans text-[16px] leading-snug mt-5" data-id="element-4909" data-mp="2clfxgxa5cpe7">
              <span className="font-semibold text-[#121A3A]" data-id="element-4910" data-mp="2ck4tawcshfmk">{pick(s.lead)}</span>
              <span className="text-[#5B6478]" data-id="element-4911" data-mp="2qjmmrp115h29">{pick(s.rest)}</span>
            </p>
            <div className="mt-auto pt-5" data-id="element-4912" data-mp="2ll30tomfs59w">
              <SourceLink label={pick(s.source)} href={s.href} data-id="element-4913" data-mp="2lk06t7tthmpr" />
            </div>
          </motion.article>)}

        <motion.figure {...reveal} transition={{
        duration: 0.3,
        delay: 0.15,
        ease: EASE
      }} className={`${CARD} md:col-span-3 xl:col-span-1 p-6 flex flex-col sm:flex-row items-center gap-6`} data-id="element-4914" data-mp="2vbxpt0r2hiz1">
          <img src={CHALLENGE_IMAGES.townhall} alt={isFR ? 'Hôtel de ville de Clarence-Rockland' : 'Clarence-Rockland City Hall'} className="w-[140px] h-[140px] rounded-[40%] object-cover shrink-0" data-id="element-4915" data-mp="34cm1vpwwh99a" />
          <div className="min-w-0" data-id="element-4916" data-mp="3sxgy7uy3q2do">
            <QuoteIcon className="w-10 h-10 text-[#A5A0F3] fill-[#A5A0F3]" aria-hidden="true" data-id="element-4917" data-mp="3swfn5qh35fvy" />
            <blockquote className="font-serif text-[1.7rem] sm:text-[2rem] leading-[1.15] text-[#121A3A] mt-2" data-id="element-4918" data-mp="3eyhduzhmg9nb">{pick(testimonial.quote)}</blockquote>
            <figcaption className="mt-4 flex items-start gap-4" data-id="element-4919" data-mp="3j1dvonzc81q7">
              <span className="w-10 h-[3px] rounded-full bg-[#4F46E5] mt-2.5 shrink-0" aria-hidden="true" data-id="element-4920" data-mp="3574jo18cp7e4" />
              <span className="font-sans text-[14px] text-[#5B6478] leading-snug" data-id="element-4921" data-mp="3a07d89d4ogub">
                {pick(testimonial.author)}
                <span className="block" data-id="element-4922" data-mp="2v7vr9ct4w0tv">{testimonial.place}</span>
              </span>
            </figcaption>
          </div>
        </motion.figure>
      </div>

      {/* Insights strip */}
      <ul className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-0 lg:divide-x divide-[#EEEEF4]" data-id="element-4923" data-mp="2mb4z3bhpd64k">
        {insights.map(it => {
        const Icon = INSIGHT_ICONS[it.key];
        return <li key={it.key} className="flex items-start gap-5 lg:px-8 first:lg:pl-0" data-id="element-4924" data-mp="36o20y3h9plci">
              <span className="w-[72px] h-[72px] rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-4925" data-mp="31ruzamvwmw0d">
                <Icon className="w-8 h-8 text-[#4F46E5]" strokeWidth={1.6} data-id="element-4926" data-mp="2sqn7mnnw8tf9" />
              </span>
              <span className="min-w-0" data-id="element-4927" data-mp="2nr0ndu9necwy">
                <span className="block font-sans text-[17px] font-semibold text-[#121A3A]" data-id="element-4928" data-mp="2ns26mm0ti5pr">{pick(it.title)}</span>
                <span className="block font-sans text-[15px] text-[#5B6478] leading-snug mt-1" data-id="element-4929" data-mp="2iz966acdtjjh">{pick(it.body)}</span>
                <span className="block mt-2.5" data-id="element-4930" data-mp="2dz4le84fy2ng">
                  <SourceLink label={pick(it.source)} href={it.href} data-id="element-4931" data-mp="293fjvz8v2boi" />
                </span>
              </span>
            </li>;
      })}
      </ul>

      {/* Transition */}
      <motion.div {...reveal} className="mt-16 text-center" data-id="element-4932" data-mp="2016chi8w2yfc">
        <p className="font-serif text-[1.5rem] sm:text-[1.9rem] leading-snug text-[#121A3A]" data-id="element-4933" data-mp="20427axpfx6zk">{pick(challengeCopy.transition1)}</p>
        <p className="font-serif text-[1.5rem] sm:text-[1.9rem] leading-snug text-[#4F46E5]" data-id="element-4934" data-mp="ki8ocxof5lrb">{pick(challengeCopy.transition2)}</p>
        <ChevronDownIcon className="w-5 h-5 text-[#A5A0F3] mx-auto mt-5" aria-hidden="true" data-id="element-4935" data-mp="oln6btvx4c7e" />
      </motion.div>
    </div>;
}